import { XMarkIcon } from "@heroicons/react/24/solid";
import avatar from "../../assets/images/Avatar.avif"
import { useTranslation } from "react-i18next";

const UserDetailsModal = ({ isOpen, onClose, user }) => {
    const { t } = useTranslation();
    return (
        <div
            className={`mt-0 fixed inset-0 z-[999] flex items-center justify-center transition-all duration-300 ${isOpen ? "visible" : "invisible"}`}
        >
            {/* Overlay */}
            <div
                onClick={onClose}
                className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
            />

            {/* Modal */}
            <div
                className={`relative z-10 w-full max-w-4xl mx-4 p-6 rounded-2xl dark:bg-bg-dark-secondary bg-bg-light-secondary shadow-glow transform transition-all duration-300 ease-out
          ${isOpen ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-6"}`}
            >
                {!user ? (
                    <p className="py-10 text-center text-gray-500">{t("userDetails.loading")}</p>
                ) : (
                    <>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-semibold text-black dark:text-white">
                                {t("userDetails.title")}
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-xl text-gray-400 duration-300 dark:text-gray-400 dark:hover:text-white hover:text-black"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Top Section: Avatar + Basic Info */}
                        <div className="flex flex-col items-center gap-6 mb-6 md:flex-row md:items-start">
                            <img
                                src={user?.avatar || avatar}
                                alt={user?.fullName}
                                className="object-cover w-24 h-24 border-2 rounded-full border-primary dark:border-primary-light"
                            />
                            <div className="flex-1">
                                <h3 className="text-xl font-semibold text-black dark:text-white">{user?.fullName}</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-300">@{user?.code}</p>
                                <p className="mt-2 text-sm text-gray-600 dark:text-text-muted-dark">{user?.email}</p>
                                {user?.phone && <p className="text-sm text-gray-600 dark:text-text-muted-dark">{user?.phone}</p>}
                                {user?.bio && <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">{user?.bio}</p>}
                            </div>
                        </div>

                        {/* Stats Section */}
                        <div className="grid grid-cols-2 gap-4 mb-6 md:grid-cols-4">
                            <div className="p-3 text-center bg-gray-100 dark:bg-bg-dark-tertiary rounded-xl">
                                <p className="font-semibold text-black dark:text-white">{user?.postsCount}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t("userDetails.posts")}</p>
                            </div>
                            <div className="p-3 text-center bg-gray-100 dark:bg-bg-dark-tertiary rounded-xl">
                                <p className="font-semibold text-black dark:text-white">{user?.followersCount}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t("userDetails.followers")}</p>
                            </div>
                            <div className="p-3 text-center bg-gray-100 dark:bg-bg-dark-tertiary rounded-xl">
                                <p className="font-semibold text-black dark:text-white">{user?.followingCount}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t("userDetails.following")}</p>
                            </div>
                            <div className="p-3 text-center bg-gray-100 dark:bg-bg-dark-tertiary rounded-xl">
                                <p className="font-semibold text-black dark:text-white">{user?.friendsCount}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t("userDetails.friends")}</p>
                            </div>
                        </div>

                        {/* Detailed Info Section */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <InfoRow label={t("userDetails.firstName")} value={user?.firstName} />
                                <InfoRow label={t("userDetails.lastName")} value={user?.lastName} />
                                <InfoRow label={t("userDetails.role")} value={user?.role} />
                                <InfoRow label={t("userDetails.verified")} value={user?.isVerified ? "Yes" : "No"} />
                                <InfoRow label={t("userDetails.blocked")} value={user?.isBlocked ? "Yes" : "No"} />
                                <InfoRow label={t("userDetails.confirmEmail")} value={user?.confirmEmail ? "Yes" : "No"} />
                                <InfoRow label={t("userDetails.googleId")} value={user?.googleId} />
                                <InfoRow label={t("userDetails.createdAt")} value={new Date(user?.createdAt).toLocaleString()} />
                                <InfoRow label={t("userDetails.updatedAt")} value={new Date(user?.updatedAt).toLocaleString()} />
                                <InfoRow label={t("userDetails.birthDate")} value={user?.birthDate ? new Date(user?.birthDate).toLocaleDateString() : "-"} />
                                <InfoRow label={t("userDetails.coverImage")} value={user?.coverImage ? <img src={user?.coverImage} alt="Cover" className="object-cover w-20 h-12 rounded-lg" /> : "-"} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// Helper Component for label + value
const InfoRow = ({ label, value }) => (
    <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-bg-dark-tertiary rounded-xl">
        <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-gray-900 dark:text-white">{value}</span>
    </div>
);

export default UserDetailsModal;
